<template>
    <div class="content-wrapper">
        <h3 class="mb-3">{{ $t('Scheduling') }}</h3>

        <!-- Tabs -->
        <ul class="nav nav-tabs mb-3">
            <li class="nav-item" v-for="t in tabs" :key="t.id">
                <a class="nav-link" :class="{ active: activeTab === t.id }" href="javascript:void(0)"
                   @click="openTab(t.id)">{{ t.label }}</a>
            </li>
        </ul>

        <!-- ============ 1. PRACTITIONER LOCATIONS ============ -->
        <div v-show="activeTab === 'loc'">
            <div class="card card-with-shadow border-0 mb-3">
                <div class="card-body">
                    <h5 class="mb-3">{{ editingLoc ? 'Edit Location' : 'Declare Starting Location' }}</h5>
                    <div class="row align-items-end">
                        <div class="col-md-2 mb-2">
                            <label class="form-label">Practitioner</label>
                            <select class="form-control" v-model="locForm.practitioner">
                                <option v-for="p in practitionerNames" :key="p" :value="p">{{ p }}</option>
                            </select>
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-label">Date</label>
                            <input type="date" class="form-control" v-model="locForm.date" />
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-label">Centre</label>
                            <select class="form-control" v-model="locForm.center">
                                <option v-for="c in CENTERS" :key="c.value" :value="c.value">{{ c.label }}</option>
                            </select>
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-label">From Time</label>
                            <input type="time" class="form-control" v-model="locForm.fromTime" />
                        </div>
                        <div class="col-md-2 mb-2">
                            <label class="form-label">To Time</label>
                            <input type="time" class="form-control" v-model="locForm.toTime" />
                        </div>
                        <div class="col-md-2 mb-2">
                            <button class="btn btn-primary btn-block" :disabled="busy" @click="setLocation">
                                {{ editingLoc ? 'Update' : 'Set Location Block' }}
                            </button>
                            <a v-if="editingLoc" href="javascript:void(0)" class="small" @click="resetLocForm">Cancel edit</a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card card-with-shadow border-0">
                <div class="card-body table-responsive">
                    <h6 class="mb-2">Upcoming location blocks</h6>
                    <table class="table table-sm table-hover mb-0">
                        <thead><tr>
                            <th>Practitioner</th><th>Date</th><th>Centre</th><th>From</th><th>To</th><th>Set by</th><th></th>
                        </tr></thead>
                        <tbody>
                            <tr v-if="!locations.length"><td colspan="7" class="text-center text-muted">No upcoming location blocks.</td></tr>
                            <template v-for="(g, gi) in locationGroups">
                                <tr :key="'g'+gi" class="bg-light">
                                    <td colspan="6"><strong>{{ g.name }} — {{ g.date }}</strong></td>
                                    <td class="text-nowrap">
                                        <button class="btn btn-sm btn-outline-danger" @click="clearAllDay(g)">Clear all for this day</button>
                                    </td>
                                </tr>
                                <tr v-for="(l, i) in g.blocks" :key="'g'+gi+'b'+i">
                                    <td>{{ l.name || l.practitioner }}</td>
                                    <td>{{ l.date }}</td>
                                    <td>{{ centerLabel(l.center) }}</td>
                                    <td>{{ l.fromTime }}</td>
                                    <td>{{ l.toTime }}</td>
                                    <td><small>{{ l.setBy }}<span v-if="l.setAt"> · {{ l.setAt }}</span></small></td>
                                    <td class="text-nowrap">
                                        <button class="btn btn-sm btn-outline-primary" @click="editLocation(l)">Edit</button>
                                        <button class="btn btn-sm btn-outline-danger" @click="clearLocation(l)">Clear</button>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- ============ 2. CENTRE ALLOCATION ============ -->
        <div v-show="activeTab === 'alloc'">
            <div class="card card-with-shadow border-0 table-responsive">
                <table class="table table-hover mb-0">
                    <thead><tr><th>Practitioner</th><th>Current centres</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                        <tr v-if="!practitioners.length"><td colspan="4" class="text-center text-muted">Loading…</td></tr>
                        <template v-for="p in practitioners">
                            <tr :key="p.name">
                                <td>{{ p.name }}</td>
                                <td>
                                    <span v-for="c in p.centers" :key="c" class="badge badge-pill badge-light border mr-1">{{ centerLabel(c) }}</span>
                                </td>
                                <td>
                                    <span :class="p.centersOverridden ? 'badge badge-warning' : 'badge badge-secondary'">
                                        {{ p.centersOverridden ? 'Custom' : 'Default' }}
                                    </span>
                                </td>
                                <td><button class="btn btn-sm btn-outline-primary" @click="toggleAllocEdit(p)">Edit</button></td>
                            </tr>
                            <tr v-if="allocEditName === p.name" :key="p.name + '-edit'">
                                <td colspan="4" class="bg-light">
                                    <div class="d-flex flex-wrap align-items-center" style="gap:16px;">
                                        <label v-for="c in CENTERS" :key="c.value" class="mb-0">
                                            <input type="checkbox" :value="c.value" v-model="allocSelected" /> {{ c.label }}
                                        </label>
                                        <button class="btn btn-sm btn-primary" :disabled="busy" @click="saveAlloc(p)">Save</button>
                                        <button v-if="p.centersOverridden" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="resetAlloc(p)">
                                            Reset to default<span v-if="p.centersDefault"> ({{ (p.centersDefault||[]).map(centerLabel).join(' · ') }})</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- ============ 3. CENTRE WORKING HOURS ============ -->
        <div v-show="activeTab === 'hours'">
            <ul class="nav nav-pills mb-3">
                <li class="nav-item"><a class="nav-link" :class="{active: hoursTab==='regular'}" href="javascript:void(0)" @click="hoursTab='regular'">Regular Hours</a></li>
                <li class="nav-item"><a class="nav-link" :class="{active: hoursTab==='exceptions'}" href="javascript:void(0)" @click="loadExceptions">Date Exceptions</a></li>
            </ul>

            <!-- Tab A: Regular hours -->
            <div v-show="hoursTab==='regular'" class="row">
                <div class="col-md-4 mb-3" v-for="c in CENTERS" :key="c.value">
                    <div class="card card-with-shadow border-0 h-100">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="mb-0">{{ c.label }}</h6>
                                <button class="btn btn-sm btn-outline-primary" @click="editHours(c.value)">Edit</button>
                            </div>
                            <p class="mb-1"><strong>Default:</strong> {{ (centerHours[c.value] && centerHours[c.value].default) || '—' }}</p>
                            <div v-if="centerHours[c.value] && centerHours[c.value].dayOverrides">
                                <small v-for="(ov, d) in centerHours[c.value].dayOverrides" :key="d" class="d-block text-muted">
                                    {{ d }}: {{ overrideText(ov) }}
                                </small>
                            </div>

                            <!-- inline edit -->
                            <div v-if="hoursEditCenter === c.value" class="mt-3 border-top pt-2">
                                <div class="form-row">
                                    <div class="col"><label class="small mb-0">Opening time</label>
                                        <input type="time" class="form-control form-control-sm" v-model="hoursForm.start" /></div>
                                    <div class="col"><label class="small mb-0">Closing time</label>
                                        <input type="time" class="form-control form-control-sm" v-model="hoursForm.end" /></div>
                                </div>
                                <div v-for="d in DAYS" :key="d.idx" class="form-row align-items-center mt-1">
                                    <div class="col-4"><small>{{ d.name }}</small></div>
                                    <div class="col-4">
                                        <select class="form-control form-control-sm" v-model="hoursForm.days[d.idx].mode">
                                            <option value="default">Follow default</option>
                                            <option value="closed">Closed</option>
                                            <option value="custom">Custom hours</option>
                                        </select>
                                    </div>
                                    <div class="col-4 d-flex" style="gap:4px;" v-if="hoursForm.days[d.idx].mode==='custom'">
                                        <input type="time" class="form-control form-control-sm" v-model="hoursForm.days[d.idx].start" />
                                        <input type="time" class="form-control form-control-sm" v-model="hoursForm.days[d.idx].end" />
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <button class="btn btn-sm btn-primary" :disabled="busy" @click="saveHours(c.value)">Save</button>
                                    <button class="btn btn-sm btn-link" @click="hoursEditCenter=null">Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tab B: Date exceptions -->
            <div v-show="hoursTab==='exceptions'">
                <div class="card card-with-shadow border-0 mb-3">
                    <div class="card-body">
                        <div class="row align-items-end">
                            <div class="col-md-3 mb-2">
                                <label class="form-label">Center</label>
                                <select class="form-control" v-model="excForm.center">
                                    <option v-for="c in CENTERS" :key="c.value" :value="c.value">{{ c.label }}</option>
                                </select>
                            </div>
                            <div class="col-md-2 mb-2"><label class="form-label">Date</label>
                                <input type="date" class="form-control" v-model="excForm.date" /></div>
                            <div class="col-md-3 mb-2">
                                <label class="form-label d-block">Status</label>
                                <label class="mr-2"><input type="radio" value="open" v-model="excForm.status" /> Open (custom)</label>
                                <label><input type="radio" value="closed" v-model="excForm.status" /> Closed</label>
                            </div>
                            <template v-if="excForm.status==='open'">
                                <div class="col-md-2 mb-2"><label class="form-label">Opening time</label>
                                    <input type="time" class="form-control" v-model="excForm.start" /></div>
                                <div class="col-md-2 mb-2"><label class="form-label">Closing time</label>
                                    <input type="time" class="form-control" v-model="excForm.end" /></div>
                            </template>
                            <div class="col-md-2 mb-2"><label class="form-label">Reason</label>
                                <input type="text" class="form-control" v-model="excForm.reason" placeholder="e.g. Diwali" /></div>
                            <div class="col-md-2 mb-2">
                                <button class="btn btn-primary btn-block" :disabled="busy" @click="setException">Set Exception</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card card-with-shadow border-0 table-responsive">
                    <table class="table table-sm table-hover mb-0">
                        <thead><tr><th>Center</th><th>Date</th><th>Hours</th><th>Reason</th><th></th></tr></thead>
                        <tbody>
                            <tr v-if="!exceptions.length"><td colspan="5" class="text-center text-muted">No upcoming exceptions.</td></tr>
                            <tr v-for="(e, i) in exceptions" :key="i">
                                <td>{{ centerLabel(e.center) }}</td><td>{{ e.date }}</td>
                                <td>{{ e.display || e.hours || '—' }}</td><td>{{ e.reason }}</td>
                                <td><button class="btn btn-sm btn-outline-danger" @click="removeException(e)">Remove</button></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- ============ 4. CALENDAR & SHEET IDs ============ -->
        <div v-show="activeTab === 'ids'">
            <div class="card card-with-shadow border-0 mb-3"><div class="card-body">
                <h6 class="mb-3">Practitioner Calendars</h6>
                <div class="form-row align-items-center mb-2" v-for="(pc, name) in cfg.practitioners" :key="name">
                    <div class="col-md-3">{{ name }} <span v-if="pc.isOverridden" class="badge badge-success">set</span></div>
                    <div class="col-md-7"><input type="text" class="form-control form-control-sm" v-model="pc.calendarId" placeholder="abc@group.calendar.google.com" /></div>
                    <div class="col-md-2"><button class="btn btn-sm btn-primary btn-block" :disabled="busy" @click="saveCalId({type:'practitioner', practitioner:name, calendarId: pc.calendarId})">Save</button></div>
                </div>
            </div></div>

            <div class="card card-with-shadow border-0 mb-3"><div class="card-body">
                <h6 class="mb-3">Room Calendars</h6>
                <template v-for="(rooms, center) in cfg.rooms">
                    <div class="form-row align-items-center mb-2" v-for="(r, idx) in rooms" :key="center + '-' + idx">
                        <div class="col-md-3">{{ centerLabel(center) }} Room {{ r.roomNumber }} <span v-if="r.isOverridden" class="badge badge-success">set</span></div>
                        <div class="col-md-7"><input type="text" class="form-control form-control-sm" v-model="r.calendarId" placeholder="abc@group.calendar.google.com" /></div>
                        <div class="col-md-2"><button class="btn btn-sm btn-primary btn-block" :disabled="busy" @click="saveCalId({type:'room', center: center, roomIndex: idx, calendarId: r.calendarId})">Save</button></div>
                    </div>
                </template>
            </div></div>

            <div class="card card-with-shadow border-0"><div class="card-body">
                <h6 class="mb-1">Google Sheet</h6>
                <p class="text-muted small mb-3">Invoice data lives in a separate tab of the same spreadsheet — only the Patient History sheet is configured here.</p>
                <div class="form-row align-items-center mb-2">
                    <div class="col-md-3">Patient History Sheet
                        <span v-if="cfg.patientSheetId" class="text-success">&#10003;</span>
                    </div>
                    <div class="col-md-4"><input type="text" class="form-control form-control-sm" v-model="cfg.patientSheetId" placeholder="Sheet ID" /></div>
                    <div class="col-md-3"><input type="text" class="form-control form-control-sm" v-model="patientSheetName" placeholder="Tab name (e.g. PatientHistory)" /></div>
                    <div class="col-md-2"><button class="btn btn-sm btn-primary btn-block" :disabled="busy" @click="saveCalId({type:'patientSheet', sheetId: cfg.patientSheetId, sheetName: patientSheetName})">Save</button></div>
                </div>
            </div></div>
        </div>

        <!-- ============ 5. PATIENT HISTORY LOOKUP ============ -->
        <div v-show="activeTab === 'patients'">
            <div class="card card-with-shadow border-0 mb-3"><div class="card-body">
                <div class="row align-items-end">
                    <div class="col-md-6 mb-2"><label class="form-label">Search (name or phone)</label>
                        <input type="text" class="form-control" v-model="patSearch" @input="debouncedPatients" placeholder="Type to search…" /></div>
                    <div class="col-md-4 mb-2"><label class="form-label">Center</label>
                        <select class="form-control" v-model="patCenter">
                            <option value="">All centers</option>
                            <option v-for="c in CENTERS" :key="c.value" :value="c.value">{{ c.label }}</option>
                        </select></div>
                </div>
            </div></div>

            <div class="card card-with-shadow border-0 table-responsive mb-3">
                <table class="table table-sm table-hover mb-0">
                    <thead><tr><th>Name</th><th>Phone</th><th>Last Center</th><th>Last Practitioner</th><th>Last Visit</th><th></th></tr></thead>
                    <tbody>
                        <tr v-if="!filteredPatients.length"><td colspan="6" class="text-center text-muted">No patients.</td></tr>
                        <tr v-for="(p, i) in filteredPatients" :key="i">
                            <td>{{ p.name }}</td><td>{{ p.phone }}</td><td>{{ centerLabel(p.lastCenter) }}</td>
                            <td>{{ p.lastPractitioner }}</td><td>{{ p.lastVisitDate }}</td>
                            <td><button class="btn btn-sm btn-outline-primary" @click="viewPatient(p)">View</button></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="patDetail" class="card card-with-shadow border-0"><div class="card-body">
                <h6 class="mb-2">{{ patDetail.history.name || patDetail.phone }} — {{ patDetail.phone }}</h6>
                <p class="mb-1"><strong>Last practitioner:</strong> {{ patDetail.history.lastPractitioner || '—' }}
                   &nbsp; <strong>Last center:</strong> {{ centerLabel(patDetail.history.lastCenter) }}
                   &nbsp; <strong>Last visit:</strong> {{ patDetail.history.lastVisitDate || '—' }}</p>
                <p v-if="patDetail.history.notes" class="text-muted mb-3">{{ patDetail.history.notes }}</p>

                <h6 class="mb-2">Invoice History</h6>
                <div v-if="patDetail.invoiceNotice" class="alert alert-warning py-2">{{ patDetail.invoiceNotice }}</div>
                <div v-else class="table-responsive">
                    <table class="table table-sm mb-0">
                        <thead><tr><th>Invoice</th><th>Date</th><th>Center</th><th>Practitioner</th><th>Services</th><th class="text-right">Amount</th><th>Status</th><th>Notes</th></tr></thead>
                        <tbody>
                            <tr v-if="!patDetail.invoices.length"><td colspan="8" class="text-center text-muted">No invoices.</td></tr>
                            <tr v-for="(inv, i) in patDetail.invoices" :key="i">
                                <td>{{ inv.invoiceId }}</td><td>{{ inv.invoiceDate }}</td><td>{{ centerLabel(inv.center) }}</td>
                                <td>{{ inv.practitioner }}</td><td>{{ inv.services }}</td>
                                <td class="text-right">{{ inv.currency || 'INR' }} {{ inv.amount }}</td>
                                <td>{{ inv.paymentStatus }}</td><td>{{ inv.notes }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div></div>
        </div>
    </div>
</template>

<script>
import { axiosPost } from "../../../../common/Helper/AxiosHelper";
import { SCHEDULING_PROXY } from "../../../Config/ApiUrl-CP";

const CENTERS = [
    { value: 'goregaon', label: 'Goregaon' },
    { value: 'bandra', label: 'Bandra' },
    { value: 'charni_road', label: 'Charni Road' },
];
const PRACTITIONERS = ['Bhoomiksha', 'Pradnya', 'Nidhi'];
const DAYS = [
    { idx: 1, name: 'Monday' }, { idx: 2, name: 'Tuesday' }, { idx: 3, name: 'Wednesday' },
    { idx: 4, name: 'Thursday' }, { idx: 5, name: 'Friday' }, { idx: 6, name: 'Saturday' }, { idx: 0, name: 'Sunday' },
];

function today() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default {
    name: "Scheduling",
    data() {
        return {
            CENTERS, DAYS,
            practitionerNames: ['Pradnya', 'Nidhi'],
            tabs: [
                { id: 'loc', label: 'Practitioner Locations' },
                { id: 'alloc', label: 'Centre Allocation' },
                { id: 'hours', label: 'Centre Working Hours' },
                { id: 'ids', label: 'Calendar & Sheet IDs' },
                { id: 'patients', label: 'Patient History Lookup' },
            ],
            activeTab: 'loc',
            busy: false,
            // section 1
            locForm: { practitioner: 'Pradnya', date: today(), center: 'goregaon', fromTime: '10:30', toTime: '13:30' },
            editingLoc: false,
            locations: [],
            // section 2
            practitioners: [],
            allocEditName: null,
            allocSelected: [],
            // section 3
            hoursTab: 'regular',
            centerHours: {},
            hoursEditCenter: null,
            hoursForm: { start: '10:30', end: '17:30', days: {} },
            exceptions: [],
            excForm: { center: 'goregaon', date: today(), status: 'closed', start: '10:00', end: '14:00', reason: '' },
            // section 4
            cfg: { practitioners: {}, rooms: {}, patientSheetId: '' },
            patientSheetName: 'PatientHistory',
            // section 5
            patSearch: '',
            patCenter: '',
            patients: [],
            patDetail: null,
            _patTimer: null,
        };
    },
    computed: {
        locationGroups() {
            const groups = {};
            (this.locations || []).forEach(l => {
                const key = `${l.practitioner || l.name}|${l.date}`;
                if (!groups[key]) {
                    groups[key] = { practitioner: l.practitioner || l.name, name: l.name || l.practitioner, date: l.date, blocks: [] };
                }
                groups[key].blocks.push(l);
            });
            return Object.values(groups);
        },
        filteredPatients() {
            if (!this.patCenter) return this.patients;
            return this.patients.filter(p => (p.lastCenter || '').toLowerCase() === this.patCenter);
        },
    },
    created() {
        this.loadLocations();
    },
    methods: {
        // ---- core ----
        async callApi(action, params = {}) {
            const res = await axiosPost(SCHEDULING_PROXY, { action, params });
            return res.data;
        },
        centerLabel(v) {
            if (!v) return '—';
            const c = CENTERS.find(x => x.value === String(v).toLowerCase());
            return c ? c.label : v;
        },
        dayName(idx) { const d = DAYS.find(x => String(x.idx) === String(idx)); return d ? d.name : idx; },
        overrideText(ov) {
            if (ov === null || ov === undefined) return 'Closed';
            if (typeof ov === 'string') return ov;
            if (typeof ov === 'object' && ov.start != null) return `${ov.start}:00 – ${ov.end}:00`;
            return String(ov);
        },
        openTab(id) {
            this.activeTab = id;
            if (id === 'alloc' && !this.practitioners.length) this.loadPractitioners();
            if (id === 'hours') this.loadCenterHours();
            if (id === 'ids') this.loadConfig();
            if (id === 'patients' && !this.patients.length) this.loadPatients();
        },

        // ---- 1. locations ----
        loadLocations() {
            this.callApi('getPractitionerLocation', { upcomingOnly: true }).then(d => {
                this.locations = (d && d.locations) ? d.locations : [];
            }).catch(this.apiErr);
        },
        setLocation() {
            if (!this.locForm.fromTime || !this.locForm.toTime) { this.$toastr.e('From and To time are required.'); return; }
            this.busy = true;
            this.callApi('setPractitionerLocation', {
                practitioner: this.locForm.practitioner, date: this.locForm.date,
                center: this.locForm.center, fromTime: this.locForm.fromTime, toTime: this.locForm.toTime,
                setBy: 'front desk',
            }).then(d => {
                if (d && d.success === false) return this.$toastr.e(d.error || 'Failed.');
                this.$toastr.s('Location block set.');
                this.resetLocForm();
                this.loadLocations();
            }).catch(this.apiErr).finally(() => { this.busy = false; });
        },
        editLocation(l) {
            this.editingLoc = true;
            this.locForm = {
                practitioner: l.practitioner || l.name, date: l.date,
                center: String(l.center).toLowerCase(), fromTime: l.fromTime, toTime: l.toTime || '',
            };
        },
        resetLocForm() {
            this.editingLoc = false;
            this.locForm = { practitioner: 'Pradnya', date: today(), center: 'goregaon', fromTime: '10:30', toTime: '13:30' };
        },
        clearLocation(l) {
            if (!confirm('Clear this location block?')) return;
            this.callApi('clearPractitionerLocation', {
                practitioner: l.practitioner || l.name, date: l.date,
                center: String(l.center).toLowerCase(), fromTime: l.fromTime,
            }).then(() => { this.$toastr.s('Block cleared.'); this.loadLocations(); }).catch(this.apiErr);
        },
        clearAllDay(g) {
            if (!confirm(`Clear all blocks for ${g.name} on ${g.date}?`)) return;
            this.callApi('clearPractitionerLocation', { practitioner: g.practitioner, date: g.date })
                .then(() => { this.$toastr.s('Day cleared.'); this.loadLocations(); }).catch(this.apiErr);
        },

        // ---- 2. allocation ----
        loadPractitioners() {
            this.callApi('listPractitioners', {}).then(d => {
                this.practitioners = (d && d.practitioners) ? d.practitioners : [];
            }).catch(this.apiErr);
        },
        toggleAllocEdit(p) {
            if (this.allocEditName === p.name) { this.allocEditName = null; return; }
            this.allocEditName = p.name;
            this.allocSelected = (p.centers || []).map(c => String(c).toLowerCase());
        },
        saveAlloc(p) {
            if (!this.allocSelected.length) { this.$toastr.e('Select at least one centre.'); return; }
            this.busy = true;
            this.callApi('updatePractitionerCenters', { practitioner: p.name, centers: this.allocSelected })
                .then(d => {
                    if (d && d.success === false) return this.$toastr.e(d.error || 'Failed.');
                    this.$toastr.s('Centres updated.');
                    this.allocEditName = null;
                    this.loadPractitioners();
                }).catch(this.apiErr).finally(() => { this.busy = false; });
        },
        resetAlloc(p) {
            this.busy = true;
            this.callApi('resetPractitionerCenters', { practitioner: p.name })
                .then(() => { this.$toastr.s('Reset to default.'); this.allocEditName = null; this.loadPractitioners(); })
                .catch(this.apiErr).finally(() => { this.busy = false; });
        },

        // ---- 3. hours ----
        loadCenterHours() {
            this.callApi('getCenterHours', {}).then(d => {
                this.centerHours = (d && d.allCenters) ? d.allCenters : {};
            }).catch(this.apiErr);
        },
        parseHours(str) {
            // Extract "HH:MM" strings from e.g. "10:30 – 17:30"; return strings (not numbers).
            const m = String(str || '').match(/\d{1,2}:\d{2}/g);
            return (m && m.length >= 2) ? { start: m[0], end: m[1] } : { start: '10:00', end: '18:00' };
        },
        editHours(center) {
            this.hoursEditCenter = center;
            const h = this.centerHours[center] || {};
            const def = this.parseHours(h.default);
            const ov = h.dayOverrides || {}; // keyed by day NAME, values are strings ("Closed" / "09:00 – 14:00")
            const days = {};
            DAYS.forEach(d => {
                const val = ov[d.name];
                if (val === undefined) days[d.idx] = { mode: 'default', start: def.start, end: def.end };
                else if (val === null || /closed/i.test(String(val))) days[d.idx] = { mode: 'closed', start: def.start, end: def.end };
                else { const p = this.parseHours(val); days[d.idx] = { mode: 'custom', start: p.start, end: p.end }; }
            });
            this.hoursForm = { start: def.start, end: def.end, days };
        },
        saveHours(center) {
            const dayOverrides = {};
            DAYS.forEach(d => {
                const row = this.hoursForm.days[d.idx];
                if (!row || row.mode === 'default') return;
                if (row.mode === 'closed') dayOverrides[d.idx] = null;
                else dayOverrides[d.idx] = { start: row.start, end: row.end }; // HH:MM strings, keyed by day index
            });
            this.busy = true;
            this.callApi('setCenterHours', { center, start: this.hoursForm.start, end: this.hoursForm.end, dayOverrides })
                .then(d => {
                    if (d && d.success === false) return this.$toastr.e(d.error || 'Failed.');
                    this.$toastr.s('Hours saved.'); this.hoursEditCenter = null; this.loadCenterHours();
                }).catch(this.apiErr).finally(() => { this.busy = false; });
        },
        loadExceptions() {
            this.hoursTab = 'exceptions';
            this.callApi('listCenterHoursExceptions', { dateFrom: today() }).then(d => {
                this.exceptions = (d && d.exceptions) ? d.exceptions : [];
            }).catch(this.apiErr);
        },
        setException() {
            if (!this.excForm.date) { this.$toastr.e('Date is required.'); return; }
            const params = { center: this.excForm.center, date: this.excForm.date, reason: this.excForm.reason };
            if (this.excForm.status === 'closed') params.start = null;
            else { params.start = this.excForm.start; params.end = this.excForm.end; }
            this.busy = true;
            this.callApi('setCenterHoursException', params).then(d => {
                if (d && d.success === false) return this.$toastr.e(d.error || 'Failed.');
                this.$toastr.s('Exception set.'); this.excForm.reason = ''; this.loadExceptions();
            }).catch(this.apiErr).finally(() => { this.busy = false; });
        },
        removeException(e) {
            if (!confirm('Remove this exception?')) return;
            this.callApi('removeCenterHoursException', { center: e.center, date: e.date })
                .then(() => { this.$toastr.s('Removed.'); this.loadExceptions(); }).catch(this.apiErr);
        },

        // ---- 4. calendar/sheet ids ----
        loadConfig() {
            this.callApi('getCalendarConfig', {}).then(d => {
                if (!d) return;
                this.cfg = {
                    practitioners: d.practitioners || {},
                    rooms: d.rooms || {},
                    patientSheetId: d.patientSheetId || '',
                };
                this.patientSheetName = d.patientSheetName || 'PatientHistory';
            }).catch(this.apiErr);
        },
        saveCalId(params) {
            this.busy = true;
            this.callApi('setCalendarId', params).then(d => {
                if (d && d.success === false) return this.$toastr.e(d.error || 'Failed.');
                this.$toastr.s('Saved.'); this.loadConfig();
            }).catch(this.apiErr).finally(() => { this.busy = false; });
        },

        // ---- 5. patients ----
        debouncedPatients() {
            clearTimeout(this._patTimer);
            this._patTimer = setTimeout(() => this.loadPatients(), 350);
        },
        loadPatients() {
            this.callApi('listPatients', { search: this.patSearch, limit: 50, offset: 0 }).then(d => {
                this.patients = (d && d.patients) ? d.patients : [];
            }).catch(this.apiErr);
        },
        viewPatient(p) {
            this.patDetail = { phone: p.phone, history: {}, invoices: [], invoiceNotice: '' };
            this.callApi('getPatientHistory', { patientPhone: p.phone }).then(d => {
                this.patDetail.history = d || {};
            }).catch(this.apiErr);
            this.callApi('getPatientInvoices', { patientPhone: p.phone }).then(d => {
                if (d && d.found === false && !(d.invoices || []).length) {
                    this.patDetail.invoiceNotice = 'Invoice data not configured — go to Calendar & Sheet IDs.';
                    this.patDetail.invoices = [];
                } else {
                    this.patDetail.invoices = (d && d.invoices) ? d.invoices : [];
                }
            }).catch(() => {
                this.patDetail.invoiceNotice = 'Invoice data not configured — go to Calendar & Sheet IDs.';
            });
        },

        apiErr(e) {
            const msg = (e && e.response && e.response.data && e.response.data.error) ? e.response.data.error : 'Could not reach the booking engine.';
            this.$toastr.e(msg);
        },
    },
};
</script>
